import React, { useEffect, useState } from "react";
import { IUserDocument } from "../../Models/IUserDocument";

// Internationalization Import
import i18n from "i18next";
import { helperMethods } from "../../helpers/helperMethods";
import { IFetchWrapperResultDto } from "../../Models/IFetchWrapperResultDto";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import { baseUrl } from "../../helpers/constants";
import ExecuteDialog from "../../Controls/ExecuteDialog";
import { IDokument } from "../../Models/IDokument";
import {
  Button,
  createTableColumn,
  TableCellLayout,
  TableColumnDefinition,
  Tooltip,
  Text,
  Link,
  Label,
  Tag,
} from "@fluentui/react-components";
import {
  ArrowResetRegular,
  DeleteRegular,
  DocumentArrowDownRegular,
  DocumentPdfRegular,
  LockClosedRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";
import { StackShim } from "@fluentui/react-migration-v8-v9";
import DownloadNotification from "../../Controls/DownloadDialog";
import DocumentViewer from "../../Controls/DocumentViewer";

interface IProductDocumentColumnsProps {
  onRefresh(): void;
  allDocuments: IDokument[] | undefined;
}

export function ProductDocumentColumns(props: IProductDocumentColumnsProps) {
  //#region Constants
  const [activeDownloadDocument, setActiveDownloadDocument] = useState<
    IDokument | IUserDocument | undefined
  >(undefined);

  const [documentProps, setDocumentProps] = useState({
    path: "",
    title: "",
    stueliId: "",
  });

  const [roles, setRoles] = useState([""]);
  const [deleteSelectedGuid, setDeleteSelectedGuid] = useState<
    string | undefined
  >(undefined);
  const [deleteSelectedUserDocGuid, setDeleteSelectedUserDocGuid] = useState<
    string | undefined
  >(undefined);

  const [changeDeleteSelectedGuid, setChangeDeleteSelectedGuid] = useState<
    string | undefined
  >(undefined);
  const [fetchWrapperResult, setFetchWrapperResult] = useState<
    IFetchWrapperResultDto | undefined
  >(undefined);

  const getUserRoles = () => {
    fetchWrapper
      .getWithAuth(`${baseUrl}/api/account/getmyroles`)
      .then((result) => {
        setRoles(result);
      })
      .catch((error) => {});
  };

  const [oldDocuments, setOldDocuments] = useState<
    (IDokument | IUserDocument)[]
  >([]);
  //#endregion

  //#region useEffects
  useEffect(() => {
    getUserRoles();
  }, []);

  useEffect(() => {
    getAllOldVersions();
  }, [props.allDocuments]);
  //#endregion

  //#region functions
  const getAllOldVersions = () => {
    if (!props.allDocuments) return;
    const allDocs: IDokument[] = props.allDocuments;

    // newest version per folder + name + extension
    const newestByGroup: {
      folder: string;
      extension: string;
      baseName: string;
      version: number;
    }[] = [];

    // collect newest version per group
    allDocs.forEach((doc) => {
      const nameParts = doc.docFileName.toLowerCase().split("_v");
      if (nameParts.length < 2) return;

      const baseName = nameParts[0];
      const versionStr = nameParts[1].split(".")[0];
      const version = Number(versionStr);

      const folder = doc.docPath.split("/").at(-2)?.toLowerCase() ?? "";
      const extension = doc.docFileExtension.toLowerCase();

      if (isNaN(version) || !folder || !baseName) return;

      const match = newestByGroup.find(
        (x) =>
          x.folder === folder &&
          x.extension === extension &&
          x.baseName === baseName
      );

      if (!match) {
        newestByGroup.push({ folder, extension, baseName, version });
        return;
      }

      if (version > match.version) match.version = version;
    });

    // collect old versions
    const oldDocs: IDokument[] = [];

    allDocs.forEach((doc) => {
      const nameParts = doc.docFileName.toLowerCase().split("_v");
      if (nameParts.length < 2) return;

      const baseName = nameParts[0];
      const versionStr = nameParts[1].split(".")[0];
      const version = Number(versionStr);

      const folder = doc.docPath.split("/").at(-2)?.toLowerCase() ?? "";
      const extension = doc.docFileExtension.toLowerCase();

      if (isNaN(version) || !folder || !baseName) return;

      const match = newestByGroup.find(
        (x) =>
          x.folder === folder &&
          x.extension === extension &&
          x.baseName === baseName
      );

      if (!match) return;

      if (version < match.version) oldDocs.push(doc);
    });

    setOldDocuments(oldDocs);
  };

  const showPdfViewer = (item: IDokument | IUserDocument) => {
    if (
      item.docFileExtension.toLowerCase().includes("pdf") ||
      item.docFileExtension.toLowerCase().includes("stl")
    ) {
      setDocumentProps({
        path:
          baseUrl + "/api/documents/GetDocumentFromPath?path=" + item.docPath,
        title: item.docFileName
          ? item.docFileName
          : item.artikelId != ""
          ? item.artikelId
          : "Dokument",
        stueliId:
          item.docFileExtension?.toLowerCase().includes("pdf") &&
          item.docPath?.includes("DOC_ETZ")
            ? item.artikelId
            : "",
      });
    }
  };

  const deleteExecuteDialog = () => {
    if (deleteSelectedGuid != undefined) {
      return (
        <ExecuteDialog
          isDialogOpen={deleteSelectedGuid != undefined}
          title={i18n.t<string>("deleteDocumentQuestion")}
          subText={""}
          primaryButtonIconName={"Delete"}
          primaryButtonText={i18n.t<string>("delete")}
          executeUrl={`${baseUrl}/api/Documents/DeleteDocument?dokumentGuid=${deleteSelectedGuid}`}
          onDismiss={() => setDeleteSelectedGuid(undefined)}
          onExecute={(fetchwrapperResult: any) => {
            setFetchWrapperResult(fetchwrapperResult);
            setDeleteSelectedGuid(undefined);
            props.onRefresh();
          }}
          onError={() => setDeleteSelectedGuid(undefined)}
        />
      );
    }
  };

  const deleteUserDocExecuteDialog = () => {
    if (deleteSelectedUserDocGuid != undefined) {
      return (
        <ExecuteDialog
          isDialogOpen={deleteSelectedUserDocGuid != undefined}
          title={i18n.t<string>("deleteDocumentQuestion")}
          subText={""}
          primaryButtonIconName={"Delete"}
          primaryButtonText={i18n.t<string>("delete")}
          executeUrl={`${baseUrl}/api/documents/delete?id=${deleteSelectedUserDocGuid}`}
          onDismiss={() => setDeleteSelectedUserDocGuid(undefined)}
          onExecute={(fetchwrapperResult: any) => {
            setFetchWrapperResult(fetchwrapperResult);
            setDeleteSelectedGuid(undefined);
            props.onRefresh();
          }}
          onError={() => setDeleteSelectedGuid(undefined)}
        />
      );
    }
  };

  const changeDeleteExecuteDialog = () => {
    if (changeDeleteSelectedGuid != undefined) {
      return (
        <ExecuteDialog
          isDialogOpen={changeDeleteSelectedGuid != undefined}
          title={i18n.t<string>("restoreDocumentQuestion")}
          subText={""}
          primaryButtonIconName={"Refresh"}
          primaryButtonText={i18n.t<string>("restore")}
          executeUrl={`${baseUrl}/api/Documents/ChangeDeletedDocument?dokumentGuid=${changeDeleteSelectedGuid}`}
          onDismiss={() => setChangeDeleteSelectedGuid(undefined)}
          onExecute={(fetchwrapperResult: any) => {
            setFetchWrapperResult(fetchwrapperResult);
            setChangeDeleteSelectedGuid(undefined);
            props.onRefresh();
          }}
          onError={() => setChangeDeleteSelectedGuid(undefined)}
        />
      );
    }
  };

  function isIDokument(item: any): item is IDokument {
    return "auftragId" in item || "artikelId" in item || "produktId" in item;
  }

  function isIUserDocument(item: any): item is IUserDocument {
    return (
      !("auftragId" in item) && !("artikelId" in item) && !("produktId" in item)
    );
  }
  //#endregion

  //#region Columns
  const auftragColumns: TableColumnDefinition<IDokument | IUserDocument>[] = [
    createTableColumn<IDokument | IUserDocument>({
      columnId: "docFileExtension",
      renderHeaderCell: () => {
        return "";
      },
      renderCell: (item: IDokument | IUserDocument) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {item.docFileExtension.toLowerCase() == "pdf" && (
                <DocumentPdfRegular />
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDokument | IUserDocument>({
      columnId: "auftragId",
      renderHeaderCell: () => {
        return i18n.t<string>("auftragId");
      },
      renderCell: (item: IDokument | IUserDocument) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {isIDokument(item) && <Text>{item.auftragId}</Text>}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDokument | IUserDocument>({
      columnId: "benennung",
      renderHeaderCell: () => {
        return i18n.t<string>("benennung");
      },
      renderCell: (item: IDokument | IUserDocument) => {
        let fileName;

        if (isIDokument(item)) {
          let docType = i18n.language.includes("de")
            ? item.docTypeDE
            : item.docTypeEN;

          let benennung = i18n.language.includes("de")
            ? item.benennungDE
            : item.benennungEN;

          if (docType && benennung) {
            fileName = docType + " - " + benennung;
          } else if (docType) {
            fileName = docType;
          } else if (benennung) {
            fileName = benennung;
          }
        } else {
          fileName = item.description;
        }

        var isOldVersion: boolean = oldDocuments.includes(item);

        return (
          <TableCellLayout truncate>
            {documentProps.path.includes(item.docPath) && (
              <DocumentViewer
                addToBedarf={[
                  (addToBedarf: any) => {
                    //setAddToBedarf(addToBedarf);
                  },
                ]}
                title={documentProps.title}
                path={documentProps.path}
                productId={undefined}
                stueliId={documentProps.stueliId}
                onDismiss={() => {
                  setDocumentProps({ title: "", path: "", stueliId: "" });
                }}
                canDownload={true}
              />
            )}

            {isOldVersion ? (
              <>
                <Tooltip
                  content={i18n.t<string>("oldDocumentVersionTooltip")}
                  relationship={"description"}
                >
                  <Tag
                    style={{ marginRight: "10px" }} /*  appearance="brand" */
                  >
                    {i18n.t<string>("oldDocumentVersionTag")}
                  </Tag>
                </Tooltip>
                <Label>{fileName}</Label>
              </>
            ) : (
              <Link
                onClick={() => {
                  if (
                    item.docFileExtension.toLowerCase().includes("pdf") ||
                    item.docFileExtension.toLowerCase().includes("stl")
                  ) {
                    showPdfViewer(item);
                  } else {
                    setActiveDownloadDocument(item);
                    helperMethods.downloadFile(item, true).finally(() => {
                      setActiveDownloadDocument(undefined);
                    });
                  }
                }}
                style={{
                  textDecoration:
                    isIDokument(item) && item.isDeleted
                      ? "line-through"
                      : undefined,
                }}
              >
                {fileName}
              </Link>
            )}
            <Label style={{ marginLeft: "10px", color: "#605e5cab" }}>
              {item.docFileName}
            </Label>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDokument | IUserDocument>({
      columnId: "created",
      renderHeaderCell: () => {
        return i18n.t<string>("created");
      },
      renderCell: (item: IDokument | IUserDocument) => {
        return (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              {item.created && (
                <Text>{new Date(item.created).toLocaleDateString()}</Text>
              )}
            </div>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<IDokument | IUserDocument>({
      columnId: "actions",
      renderHeaderCell: () => {
        return i18n.t<string>("actions");
      },
      renderCell: (item: IDokument | IUserDocument) => {
        var isOldVersion: boolean = oldDocuments.includes(item);
        return isOldVersion ? (
          <></>
        ) : (
          <TableCellLayout>
            <div className="lueb-centerVertical">
              <StackShim horizontal>
                {activeDownloadDocument == item && <DownloadNotification />}

                <Tooltip
                  relationship="label"
                  content={i18n.t<string>("download")}
                >
                  <Button
                    onClick={() => {
                      setActiveDownloadDocument(item);
                      helperMethods.downloadFile(item, true).finally(() => {
                        setActiveDownloadDocument(undefined);
                      });
                    }}
                    appearance="subtle"
                    icon={<DocumentArrowDownRegular color="#ff6600" />}
                  />
                </Tooltip>

                {!isIDokument(item) && item.canCreateFreigabe && (
                  <Tooltip
                    relationship="label"
                    content={i18n.t<string>("shareObject")!}
                  >
                    <Button
                      appearance="subtle"
                      icon={
                        item.hasFreigaben ? (
                          <PeopleTeamRegular color="#ff6600" />
                        ) : (
                          <LockClosedRegular color="#ff6600" />
                        )
                      }
                      onClick={() => {
                        //setFreigabeGuid(item.userProduktGuid);
                      }}
                    ></Button>
                  </Tooltip>
                )}

                {roles.includes("Vertriebsadmin") &&
                  isIDokument(item) &&
                  item.isDeleted == true && (
                    <Tooltip
                      relationship="label"
                      content={i18n.t<string>("restoreDocument")}
                    >
                      <Button
                        onClick={() => {
                          setChangeDeleteSelectedGuid(item.dokumentGuid);
                        }}
                        appearance="subtle"
                        icon={<ArrowResetRegular color="#ff6600" />}
                      />
                    </Tooltip>
                  )}

                {roles.includes("Vertriebsadmin") &&
                  isIDokument(item) &&
                  item.isDeleted == false && (
                    <Tooltip
                      relationship="label"
                      content={i18n.t<string>("deleteDocument")}
                    >
                      <Button
                        onClick={() => {
                          setDeleteSelectedGuid(item.dokumentGuid);
                        }}
                        appearance="subtle"
                        icon={<DeleteRegular color="#ff6600" />}
                      />
                    </Tooltip>
                  )}

                {isIUserDocument(item) && item.canDeleteObject && (
                  <Tooltip
                    relationship="label"
                    content={i18n.t<string>("deleteDocument")}
                  >
                    <Button
                      onClick={() => {
                        setDeleteSelectedUserDocGuid(item.userObjectGuid);
                      }}
                      appearance="subtle"
                      icon={<DeleteRegular color="#ff6600" />}
                    />
                  </Tooltip>
                )}
              </StackShim>
            </div>
          </TableCellLayout>
        );
      },
    }),
  ];

  const produktIdColumn = createTableColumn<IDokument | IUserDocument>({
    columnId: "productId",
    renderHeaderCell: () => {
      return i18n.t<string>("productId");
    },
    renderCell: (item: IDokument | IUserDocument) => {
      return (
        <TableCellLayout>
          <div className="lueb-centerVertical">
            {isIDokument(item) && <Text>{item.produktId}</Text>}
          </div>
        </TableCellLayout>
      );
    },
  });

  const articleIdColumn = createTableColumn<IDokument | IUserDocument>({
    columnId: "artikelId",
    renderHeaderCell: () => {
      return i18n.t<string>("artikelId");
    },
    renderCell: (item: IDokument | IUserDocument) => {
      return (
        <TableCellLayout>
          <div className="lueb-centerVertical">
            {isIDokument(item) && <Text>{item.artikelId}</Text>}
          </div>
        </TableCellLayout>
      );
    },
  });

  const exemplarColumns = auftragColumns.map((x) => x);
  const artikelColumns = auftragColumns.map((x) => x);
  const userColumns = auftragColumns.map((x) => x);

  let index = exemplarColumns.findIndex((d) => d.columnId === "auftragId");
  exemplarColumns.splice(index, 1, produktIdColumn);
  artikelColumns.splice(index, 1, articleIdColumn);
  userColumns.splice(index, 1);

  return {
    changeDeleteExecuteDialog: () => {
      return changeDeleteExecuteDialog();
    },
    deleteExecuteDialog: () => {
      return deleteExecuteDialog();
    },
    deleteUserDocExecuteDialog: () => {
      return deleteUserDocExecuteDialog();
    },
    getColumns: (type: string) => {
      if (type == "Auftrag") {
        return auftragColumns;
      } else if (type == "Artikel") {
        return artikelColumns;
      } else if (type == "Exemplar") {
        return exemplarColumns;
      } else if (type == "User") {
        return userColumns;
      } else {
        return [];
      }
    },
  };
}
//#endregion
export default ProductDocumentColumns;
